// Compresses an image file to a small JPEG data URI, entirely in the
// browser, before it's uploaded to Backblaze B2 (see functions/_lib/b2.js) —
// keeps photo posts small and fast without needing any server-side
// processing.
//
// targetAspect (width/height, e.g. 4/5 to match .post-img's aspect-ratio in
// styles.css) center-crops the source to that ratio *before* resizing, so
// the stored file itself is already the right shape. Previously this only
// resized (preserving whatever ratio the original photo happened to be),
// and every post photo only *looked* uniform because the feed's CSS
// (background-size:cover) visually clips them at render time — the actual
// uploaded file underneath was never touched. That's fine for the feed
// itself, but any other surface, or the raw file, still showed the
// original uncropped shape. Passing targetAspect makes the crop real.
export function fileToCompressedDataURI(file, maxDim = 1000, quality = 0.72, targetAspect = null) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      // Source rectangle to read from the original image — defaults to the
      // whole image, narrowed to a centered crop when targetAspect is set.
      let sx = 0, sy = 0, sWidth = img.width, sHeight = img.height;

      if (targetAspect) {
        const sourceAspect = img.width / img.height;
        if (sourceAspect > targetAspect) {
          // Wider than the target ratio — crop the left/right edges.
          sWidth = Math.round(img.height * targetAspect);
          sx = Math.round((img.width - sWidth) / 2);
        } else if (sourceAspect < targetAspect) {
          // Taller than the target ratio — crop the top/bottom edges.
          sHeight = Math.round(img.width / targetAspect);
          sy = Math.round((img.height - sHeight) / 2);
        }
      }

      let width = sWidth, height = sHeight;
      if (width > height && width > maxDim) { height = Math.round(height * (maxDim / width)); width = maxDim; }
      else if (height > maxDim) { width = Math.round(width * (maxDim / height)); height = maxDim; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, sx, sy, sWidth, sHeight, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// The fixed aspect ratio every feed post photo is cropped to before upload —
// keep this in sync with `.post-img{aspect-ratio:4/5}` in styles.css.
export const POST_PHOTO_ASPECT = 4 / 5;
